import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Edit,
  Trash2,
  Save,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Package,
  MapPin,
  Building,
  Loader,
} from "lucide-react";
import { Department, Landmark, Kiosk } from "../types";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <div>{children}</div>}
    </div>
  );
}

const AdminPanel: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [landmarks, setLandmarks] = useState<Landmark[]>([]);
  const [kiosks, setKiosks] = useState<Kiosk[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [editType, setEditType] = useState<"department" | "landmark" | "kiosk">(
    "department",
  );
  const [isAddingNew, setIsAddingNew] = useState(false);

  // Map states
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any | null>(null);
  const markerRef = useRef<any | null>(null);
  const [mapOpen, setMapOpen] = useState(false);
  const [selectedCoordinates, setSelectedCoordinates] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);

  // Function to load existing pins from database
  const loadExistingPins = async (map: any, mapboxgl: any) => {
    try {
      // Fetch all departments, landmarks, and kiosks
      const [deptRes, landmarkRes, kioskRes] = await Promise.all([
        fetch("/api/departments"),
        fetch("/api/landmarks"),
        fetch("/api/kiosks"),
      ]);

      const deptData = await deptRes.json();
      const landmarkData = await landmarkRes.json();
      const kioskData = await kioskRes.json();

      // Add department markers (blue)
      if (deptData.data) {
        deptData.data.forEach((dept: any) => {
          if (dept.coordinates?.latitude && dept.coordinates?.longitude) {
            new mapboxgl.Marker({ color: "#3b82f6" })
              .setLngLat([
                dept.coordinates.longitude,
                dept.coordinates.latitude,
              ])
              .setPopup(
                new mapboxgl.Popup().setHTML(
                  `<strong>${dept.name}</strong><br/><small>Department</small>`,
                ),
              )
              .addTo(map);
          }
        });
      }

      // Add landmark markers (green)
      if (landmarkData.data) {
        landmarkData.data.forEach((landmark: any) => {
          if (
            landmark.coordinates?.latitude &&
            landmark.coordinates?.longitude
          ) {
            new mapboxgl.Marker({ color: "#10b981" })
              .setLngLat([
                landmark.coordinates.longitude,
                landmark.coordinates.latitude,
              ])
              .setPopup(
                new mapboxgl.Popup().setHTML(
                  `<strong>${landmark.name}</strong><br/><small>Landmark</small>`,
                ),
              )
              .addTo(map);
          }
        });
      }

      // Add kiosk markers (purple)
      if (kioskData.data) {
        kioskData.data.forEach((kiosk: any) => {
          if (kiosk.coordinates?.latitude && kiosk.coordinates?.longitude) {
            new mapboxgl.Marker({ color: "#8b5cf6" })
              .setLngLat([
                kiosk.coordinates.longitude,
                kiosk.coordinates.latitude,
              ])
              .setPopup(
                new mapboxgl.Popup().setHTML(
                  `<strong>${kiosk.name}</strong><br/><small>Kiosk</small>`,
                ),
              )
              .addTo(map);
          }
        });
      }
    } catch (error) {
      console.error("Error loading existing pins:", error);
    }
  };

  // Initialize map
  useEffect(() => {
    if (!mapOpen || !mapContainer.current || mapRef.current) return;

    // Import mapbox dynamically
    import("mapbox-gl").then((mapboxgl) => {
      mapboxgl.default.accessToken =
        "pk.eyJ1IjoiaW5hYW11cnJlaG1hbiIsImEiOiJjbWxxbWtldWUwMmRyM2ZzYmF3Z2NyeXNtIn0.QdmkobugXfeqHrHZXntbtQ";

      const map = new mapboxgl.default.Map({
        container: mapContainer.current!,
        style: "mapbox://styles/mapbox/standard",
        center: [46.624, 24.812], // Hospital coordinates
        zoom: 17,
      });

      map.on("load", () => {
        // Add GeoJSON source for ground floor data
        map.addSource("ground-floor", {
          type: "geojson",
          data: "/ground_floor_combined.geojson",
        });

        // Zone fills
        map.addLayer({
          id: "zones-fill",
          type: "fill",
          source: "ground-floor",
          filter: ["==", ["get", "layer"], "svg_zone"],
          paint: {
            "fill-color": [
              "case",
              ["==", ["get", "name"], "beds"],
              "#fcb1dd",
              ["==", ["get", "name"], "Pediatric ER"],
              "#90EE90",
              ["==", ["get", "name"], "Main Corridors"],
              "#ffffff",
              ["==", ["get", "name"], "Pharmacy"],
              "#90EE90",
              "#d6ffff",
            ],
            "fill-opacity": 0.5,
          },
        });

        // Zone borders
        map.addLayer({
          id: "zones-outline",
          type: "line",
          source: "ground-floor",
          filter: ["==", ["get", "layer"], "svg_zone"],
          paint: {
            "line-color": "#808080",
            "line-width": 0.85,
            "line-opacity": 1,
          },
        });

        // POI fills
        map.addLayer({
          id: "poi-fill",
          type: "fill",
          source: "ground-floor",
          filter: ["==", ["get", "layer"], "poi_zone"],
          paint: {
            "fill-color": "#00ffff",
            "fill-opacity": 0.35,
          },
        });

        // POI borders
        map.addLayer({
          id: "poi-outline",
          type: "line",
          source: "ground-floor",
          filter: ["==", ["get", "layer"], "poi_zone"],
          paint: {
            "line-color": "#808080",
            "line-width": 0.75,
            "line-opacity": 1,
          },
        });

        // Load and display existing pins from database
        loadExistingPins(map, mapboxgl.default);
      });

      map.on("click", (e) => {
        const { lng, lat } = e.lngLat;
        const roundedLat = Math.round(lat * 1000000) / 1000000;
        const roundedLng = Math.round(lng * 1000000) / 1000000;
        setSelectedCoordinates({ latitude: roundedLat, longitude: roundedLng });

        // Remove old marker
        if (markerRef.current) {
          markerRef.current.remove();
        }

        // Add new marker
        markerRef.current = new mapboxgl.default.Marker({ color: "#ef4444" })
          .setLngLat([lng, lat])
          .addTo(map);
      });

      mapRef.current = map;
    });

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [mapOpen]);

  // Fetch data
  const fetchData = async () => {
    setLoading(true);
    try {
      const [deptRes, landmarkRes, kioskRes] = await Promise.all([
        fetch("/api/departments"),
        fetch("/api/landmarks"),
        fetch("/api/kiosks"),
      ]);

      if (deptRes.ok)
        setDepartments(await deptRes.json().then((d) => d.data || []));
      if (landmarkRes.ok)
        setLandmarks(await landmarkRes.json().then((d) => d.data || []));
      if (kioskRes.ok)
        setKiosks(await kioskRes.json().then((d) => d.data || []));
    } catch (error) {
      setMessage({ type: "error", text: "Failed to fetch data" });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Handle edit
  const handleEdit = (item: any, type: "department" | "landmark" | "kiosk") => {
    setEditingItem({ ...item, floor: item.floor || "G" });
    setEditType(type);
    setIsAddingNew(false);
    setEditDialogOpen(true);
    setSelectedCoordinates({
      latitude: item.coordinates?.latitude || item.coordinates?.y || 0,
      longitude: item.coordinates?.longitude || item.coordinates?.x || 0,
    });
  };

  // Handle add new
  const handleAddNew = (type: "department" | "landmark" | "kiosk") => {
    setEditType(type);
    setIsAddingNew(true);
    setEditingItem({
      name: "",
      floor: "G",
      category: type === "department" ? "" : undefined,
      type: type === "landmark" ? "" : undefined,
      corridor: "North",
      description: "",
      status: type === "kiosk" ? "operational" : undefined,
      is_default: false,
    });
    setSelectedCoordinates({ latitude: 24.812, longitude: 46.624 });
    setEditDialogOpen(true);
  };

  // Coordinate validation function
  const validateCoordinates = (lat: number, lon: number): string | null => {
    if (isNaN(lat) || isNaN(lon)) {
      return "Coordinates must be valid numbers";
    }
    if (lat < -90 || lat > 90) {
      return "Latitude must be between -90 and 90 degrees";
    }
    if (lon < -180 || lon > 180) {
      return "Longitude must be between -180 and 180 degrees";
    }
    return null;
  };

  // Handle save
  const handleSave = async () => {
    if (!editingItem || !selectedCoordinates) {
      setMessage({ type: "error", text: "Please set coordinates" });
      return;
    }

    if (!editingItem.name || editingItem.name.trim() === "") {
      setMessage({ type: "error", text: "Please enter a name" });
      return;
    }

    // Validate coordinates
    const validationError = validateCoordinates(
      selectedCoordinates.latitude,
      selectedCoordinates.longitude,
    );

    if (validationError) {
      setMessage({ type: "error", text: validationError });
      return;
    }

    const endpoint = isAddingNew
      ? `/api/${editType}s`
      : `/api/${editType}s/${editingItem.id}`;

    try {
      console.log("Saving to:", endpoint);

      const payload: any = {
        name: editingItem.name,
        // Send coordinates in BOTH formats to ensure compatibility
        x: selectedCoordinates.longitude,
        y: selectedCoordinates.latitude,
        latitude: selectedCoordinates.latitude,
        longitude: selectedCoordinates.longitude,
        floor: editingItem.floor || "G",
      };

      if (editType === "department") {
        payload.category = editingItem.category || "Other";
        payload.serviceType = editingItem.serviceType || "General";
        payload.corridor = editingItem.corridor || "North";
        payload.description = editingItem.description || "";
      } else if (editType === "landmark") {
        payload.type = editingItem.type || "General";
        payload.corridor = editingItem.corridor || "North";
        payload.description = editingItem.description || "";
      } else if (editType === "kiosk") {
        payload.corridor = editingItem.corridor || "North";
        payload.status = editingItem.status || "operational";
        payload.description = editingItem.description || "";
        payload.is_default = editingItem.is_default || false;
      }

      console.log("Payload:", JSON.stringify(payload, null, 2));

      const response = await fetch(endpoint, {
        method: isAddingNew ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      console.log("Response:", JSON.stringify(data, null, 2));

      if (response.ok && data.success) {
        setMessage({
          type: "success",
          text: `${editType} ${isAddingNew ? "created" : "updated"} successfully`,
        });
        setEditDialogOpen(false);
        setEditingItem(null);
        setSelectedCoordinates(null);
        setIsAddingNew(false);
        setMapOpen(false);
        // Refresh data after successful save
        await fetchData();
      } else {
        setMessage({
          type: "error",
          text: data.error || data.message || data.details || "Failed to save",
        });
      }
    } catch (error) {
      console.error("Save error:", error);
      setMessage({
        type: "error",
        text: error instanceof Error ? error.message : "Error saving data",
      });
    }
  };

  // Handle delete
  const handleDelete = async (
    id: string,
    type: "department" | "landmark" | "kiosk",
  ) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;

    try {
      const response = await fetch(`/api/${type}s/${id}`, {
        method: "DELETE",
      });

      if (response.ok) {
        setMessage({ type: "success", text: `${type} deleted successfully` });
        fetchData();
      }
    } catch (error) {
      setMessage({ type: "error", text: "Failed to delete" });
    }
  };

  // Table Component
  const renderTable = (
    items: any[],
    type: "department" | "landmark" | "kiosk",
  ) => (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="overflow-x-auto rounded-xl border"
      style={{ borderColor: "var(--surface-3)" }}
    >
      <table className="w-full">
        <thead className="bg-gradient-to-r from-primary-600 to-primary-700 text-white">
          <tr>
            <th className="px-6 py-4 text-left text-sm font-semibold">Name</th>
            <th className="px-6 py-4 text-left text-sm font-semibold hidden md:table-cell">
              Floor
            </th>
            <th className="px-6 py-4 text-left text-sm font-semibold hidden md:table-cell">
              Latitude
            </th>
            <th className="px-6 py-4 text-left text-sm font-semibold hidden md:table-cell">
              Longitude
            </th>
            <th className="px-6 py-4 text-right text-sm font-semibold">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y" style={{ borderColor: "var(--surface-3)" }}>
          <AnimatePresence>
            {items.map((item, index) => (
              <motion.tr
                key={item.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ delay: index * 0.05 }}
                className="transition-colors"
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "var(--surface-3)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "")
                }
              >
                <td
                  className="px-6 py-4 font-semibold"
                  style={{ color: "var(--color-text-primary)" }}
                >
                  {item.name}
                </td>
                <td
                  className="px-6 py-4 hidden md:table-cell text-sm"
                  style={{ color: "var(--color-text-secondary)" }}
                >
                  <span
                    className="px-2 py-0.5 rounded-full text-xs font-bold"
                    style={{ backgroundColor: "#1e3a5f", color: "#60a5fa" }}
                  >
                    {item.floor || "G"}
                  </span>
                </td>
                <td
                  className="px-6 py-4 hidden md:table-cell text-sm"
                  style={{ color: "var(--color-text-secondary)" }}
                >
                  {item.coordinates?.latitude?.toFixed(4) || "N/A"}
                </td>
                <td
                  className="px-6 py-4 hidden md:table-cell text-sm"
                  style={{ color: "var(--color-text-secondary)" }}
                >
                  {item.coordinates?.longitude?.toFixed(4) || "N/A"}
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleEdit(item, type)}
                      className="p-2 text-primary-600 hover:bg-primary-100 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Edit size={18} />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleDelete(item.id, type)}
                      className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={18} />
                    </motion.button>
                  </div>
                </td>
              </motion.tr>
            ))}
          </AnimatePresence>
        </tbody>
      </table>
      {items.length === 0 && (
        <div
          className="text-center py-8"
          style={{ color: "var(--color-text-muted)" }}
        >
          No {type}s found
        </div>
      )}
    </motion.div>
  );

  const tabs = [
    {
      id: "departments",
      label: "Departments",
      icon: Building,
      count: departments.length,
    },
    {
      id: "landmarks",
      label: "Landmarks",
      icon: MapPin,
      count: landmarks.length,
    },
    { id: "kiosks", label: "Kiosks", icon: Package, count: kiosks.length },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      style={{
        background: "var(--surface-1)",
        minHeight: "100vh",
        padding: "2rem",
      }}
    >
      {/* Header */}
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-8"
      >
        <div>
          <h1
            className="text-3xl md:text-4xl font-bold mb-2"
            style={{ color: "var(--color-text-primary)" }}
          >
            ⚙️ Admin Panel
          </h1>
          <p style={{ color: "var(--color-text-secondary)" }}>
            Manage hospital coordinates and locations
          </p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={fetchData}
          disabled={loading}
          className="btn btn-primary flex items-center gap-2"
        >
          <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
          Refresh
        </motion.button>
      </motion.div>

      {/* Message Alert */}
      <AnimatePresence>
        {message && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`mb-6 p-4 rounded-lg flex items-center gap-3 border ${
              message.type === "error"
                ? "bg-red-900/30 text-red-300 border-red-700"
                : "bg-green-900/30 text-green-300 border-green-700"
            }`}
          >
            {message.type === "error" ? (
              <AlertCircle size={20} />
            ) : (
              <CheckCircle size={20} />
            )}
            <span className="font-medium">{message.text}</span>
            <button
              onClick={() => setMessage(null)}
              className="ml-auto text-lg hover:opacity-70 transition-opacity"
            >
              ×
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {loading ? (
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="flex justify-center items-center py-16 text-primary-600"
        >
          <Loader size={48} />
        </motion.div>
      ) : (
        <>
          {/* Tabs */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.3 }}
            className="card mb-6"
            style={{
              backgroundColor: "var(--surface-2)",
              borderRadius: 12,
              padding: 16,
            }}
          >
            <div
              className="flex gap-2 border-b"
              style={{ borderColor: "var(--surface-3)" }}
            >
              {tabs.map((tab, idx) => {
                const Icon = tab.icon;
                const isActive = tabValue === idx;
                return (
                  <motion.button
                    key={tab.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setTabValue(idx)}
                    className={`flex items-center gap-2 px-4 md:px-6 py-3 font-semibold text-sm md:text-base relative transition-colors`}
                    style={{
                      color: isActive ? "#3B82F6" : "var(--color-text-muted)",
                    }}
                  >
                    <Icon size={18} />
                    <span className="hidden sm:inline">{tab.label}</span>
                    <span
                      className="text-xs rounded-full px-2 py-0.5 font-bold"
                      style={{
                        backgroundColor: "var(--surface-4)",
                        color: "var(--color-text-secondary)",
                      }}
                    >
                      {tab.count}
                    </span>
                    {isActive && (
                      <motion.div
                        layoutId="tab-indicator"
                        className="absolute bottom-0 left-0 right-0 h-1 bg-primary-600"
                      />
                    )}
                  </motion.button>
                );
              })}
            </div>

            {/* Tab Content */}
            <div className="pt-6">
              <TabPanel value={tabValue} index={0}>
                <div className="mb-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleAddNew("department")}
                    className="btn btn-primary flex items-center gap-2"
                  >
                    <Building size={18} />
                    Add New Department
                  </motion.button>
                </div>
                {renderTable(departments, "department")}
              </TabPanel>
              <TabPanel value={tabValue} index={1}>
                <div className="mb-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleAddNew("landmark")}
                    className="btn btn-primary flex items-center gap-2"
                  >
                    <MapPin size={18} />
                    Add New Landmark
                  </motion.button>
                </div>
                {renderTable(landmarks, "landmark")}
              </TabPanel>
              <TabPanel value={tabValue} index={2}>
                <div className="mb-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleAddNew("kiosk")}
                    className="btn btn-primary flex items-center gap-2"
                  >
                    <Package size={18} />
                    Add New Kiosk
                  </motion.button>
                </div>
                {renderTable(kiosks, "kiosk")}
              </TabPanel>
            </div>
          </motion.div>
        </>
      )}

      {/* Edit Dialog */}
      <AnimatePresence>
        {editDialogOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setEditDialogOpen(false)}
              className="fixed inset-0 bg-black/50 z-40"
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <motion.div
                className="rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
                style={{ backgroundColor: "var(--surface-2)" }}
              >
                {/* Header */}
                <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white px-6 py-4 sticky top-0">
                  <h2 className="text-xl font-bold capitalize">
                    {isAddingNew ? "Add New" : "Edit"} {editType}
                  </h2>
                </div>

                {/* Content */}
                <div className="p-6 space-y-4">
                  <div>
                    <label
                      className="block text-sm font-semibold mb-2"
                      style={{ color: "var(--color-text-primary)" }}
                    >
                      Name
                    </label>
                    <input
                      type="text"
                      value={editingItem?.name || ""}
                      onChange={(e) =>
                        setEditingItem((prev: any) =>
                          prev ? { ...prev, name: e.target.value } : prev,
                        )
                      }
                      className="input w-full"
                      style={{
                        backgroundColor: "var(--surface-3)",
                        border: "1px solid var(--surface-4)",
                        color: "var(--color-text-primary)",
                      }}
                      placeholder="Enter name"
                    />
                  </div>

                  {/* Floor Level dropdown — shown for all types */}
                  <div>
                    <label
                      className="block text-sm font-semibold mb-2"
                      style={{ color: "var(--color-text-primary)" }}
                    >
                      Floor Level
                    </label>
                    <select
                      value={editingItem?.floor || "G"}
                      onChange={(e) =>
                        setEditingItem((prev: any) =>
                          prev ? { ...prev, floor: e.target.value } : prev,
                        )
                      }
                      className="input w-full"
                      style={{
                        backgroundColor: "var(--surface-3)",
                        border: "1px solid var(--surface-4)",
                        color: "var(--color-text-primary)",
                      }}
                    >
                      {[
                        "B4",
                        "B3",
                        "B2",
                        "B1",
                        "G",
                        "1",
                        "2",
                        "3",
                        "4",
                        "5",
                        "6",
                        "7",
                        "8",
                        "9",
                        "10",
                        "11",
                        "12",
                        "13",
                        "14",
                        "15",
                        "16",
                      ].map((f) => (
                        <option key={f} value={f}>
                          {f}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Description field — shown for departments and landmarks */}
                  {editType !== "kiosk" && (
                    <div>
                      <label
                        className="block text-sm font-semibold mb-2"
                        style={{ color: "var(--color-text-primary)" }}
                      >
                        Description
                        <span
                          className="ml-2 text-xs font-normal"
                          style={{ color: "var(--color-text-muted)" }}
                        >
                          (English or Arabic)
                        </span>
                      </label>
                      <textarea
                        value={editingItem?.description || ""}
                        onChange={(e) =>
                          setEditingItem((prev: any) =>
                            prev
                              ? { ...prev, description: e.target.value }
                              : prev,
                          )
                        }
                        className="input w-full resize-none"
                        style={{
                          backgroundColor: "var(--surface-3)",
                          border: "1px solid var(--surface-4)",
                          color: "var(--color-text-primary)",
                        }}
                        rows={3}
                        placeholder="Enter description (optional)"
                        dir="auto"
                      />
                    </div>
                  )}

                  <div>
                    <label
                      className="block text-sm font-semibold mb-2"
                      style={{ color: "var(--color-text-primary)" }}
                    >
                      Coordinates
                    </label>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="number"
                        step="0.0001"
                        value={selectedCoordinates?.latitude || ""}
                        onChange={(e) =>
                          setSelectedCoordinates({
                            ...selectedCoordinates!,
                            latitude: parseFloat(e.target.value),
                          })
                        }
                        className="input flex-1"
                        style={{
                          backgroundColor: "var(--surface-3)",
                          border: "1px solid var(--surface-4)",
                          color: "var(--color-text-primary)",
                        }}
                        placeholder="Latitude"
                      />
                      <input
                        type="number"
                        step="0.0001"
                        value={selectedCoordinates?.longitude || ""}
                        onChange={(e) =>
                          setSelectedCoordinates({
                            ...selectedCoordinates!,
                            longitude: parseFloat(e.target.value),
                          })
                        }
                        className="input flex-1"
                        style={{
                          backgroundColor: "var(--surface-3)",
                          border: "1px solid var(--surface-4)",
                          color: "var(--color-text-primary)",
                        }}
                        placeholder="Longitude"
                      />
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setMapOpen(!mapOpen)}
                      className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <MapPin size={18} />
                      {mapOpen ? "Hide Map" : "Pick from Map"}
                    </motion.button>
                  </div>

                  {/* Map Container */}
                  {mapOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 300, opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="rounded-lg overflow-hidden border-2 border-blue-300"
                    >
                      <div ref={mapContainer} className="w-full h-full" />
                    </motion.div>
                  )}

                  {editType === "kiosk" && (
                    <div>
                      <label
                        className="block text-sm font-semibold mb-2"
                        style={{ color: "var(--color-text-primary)" }}
                      >
                        Set as Default KIOSK
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={editingItem?.is_default || false}
                          onChange={(e) =>
                            setEditingItem((prev: any) =>
                              prev
                                ? { ...prev, is_default: e.target.checked }
                                : prev,
                            )
                          }
                          className="w-4 h-4 rounded"
                          style={{ borderColor: "var(--surface-4)" }}
                        />
                        <span
                          className="text-sm"
                          style={{ color: "var(--color-text-secondary)" }}
                        >
                          Mark as default starting point
                        </span>
                      </div>
                    </div>
                  )}

                  <div
                    className="p-4 rounded-lg border"
                    style={{
                      backgroundColor: "#1e3a5f",
                      borderColor: "#3B82F6",
                      color: "#93c5fd",
                    }}
                  >
                    <p className="text-sm">
                      💡 Click on the map to select coordinates or enter them
                      manually
                    </p>
                  </div>

                  {selectedCoordinates && (
                    <div
                      className="px-4 py-3 rounded-lg flex items-center gap-2 border"
                      style={{
                        backgroundColor: "#052e16",
                        borderColor: "#16a34a",
                        color: "#86efac",
                      }}
                    >
                      <MapPin size={18} style={{ color: "#86efac" }} />
                      <span className="text-sm font-semibold">
                        📍 {selectedCoordinates.latitude.toFixed(6)},{" "}
                        {selectedCoordinates.longitude.toFixed(6)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div
                  className="flex gap-3 p-6 border-t sticky bottom-0"
                  style={{
                    backgroundColor: "var(--surface-3)",
                    borderColor: "var(--surface-4)",
                  }}
                >
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setEditDialogOpen(false)}
                    className="flex-1 px-4 py-2 rounded-lg font-semibold transition-colors"
                    style={{
                      backgroundColor: "var(--surface-4)",
                      border: "1px solid var(--surface-4)",
                      color: "var(--color-text-primary)",
                    }}
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleSave}
                    className="flex-1 btn btn-primary flex items-center justify-center gap-2"
                  >
                    <Save size={18} />
                    Save
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default AdminPanel;
